import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import CookieBanner from "@/components/site/CookieBanner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { useVersionPoll } from "@/lib/useVersionPoll";
import ProtectedRoute from "@/auth/ProtectedRoute";
// Eager: the two most common entry points stay in the main bundle.
import MarketingHomePage from "@/pages/MarketingHomePage";
import LoginPage from "@/pages/LoginPage";

// Lazy route chunks. If a deploy replaces chunk files mid-session, the stale
// client reloads once to pick up the new manifest instead of crashing.
const lazyLoad = (importer) =>
  lazy(() =>
    importer().catch((err) => {
      if (!sessionStorage.getItem("chunk-reloaded")) {
        sessionStorage.setItem("chunk-reloaded", "1");
        window.location.reload();
        return new Promise(() => {});
      }
      throw err;
    })
  );

const RegisterPage = lazyLoad(() => import("@/pages/RegisterPage"));
const DashboardPage = lazyLoad(() => import("@/pages/DashboardPage"));
const NewLoanPage = lazyLoad(() => import("@/pages/NewLoanPage"));
const LoansListPage = lazyLoad(() => import("@/pages/LoansListPage"));
const ArchivedLoansPage = lazyLoad(() => import("@/pages/ArchivedLoansPage"));
const MCAPipelinePage = lazyLoad(() => import("@/pages/MCAPipelinePage"));
const LoanDetailPage = lazyLoad(() => import("@/pages/LoanDetailPage"));
const ApplicationFormPage = lazyLoad(() => import("@/pages/ApplicationFormPage"));
const TeamPage = lazyLoad(() => import("@/pages/TeamPage"));
const InvestorsPage = lazyLoad(() => import("@/pages/InvestorsPage"));
const LendersPage = lazyLoad(() => import("@/pages/LendersPage"));
const BrokerSettingsPage = lazyLoad(() => import("@/pages/BrokerSettingsPage"));
const CommissionsPage = lazyLoad(() => import("@/pages/CommissionsPage"));
const PayoutsPage = lazyLoad(() => import("@/pages/PayoutsPage"));
const MarketplacePage = lazyLoad(() => import("@/pages/MarketplacePage"));
const MarketplaceLoanDetailPage = lazyLoad(() => import("@/pages/MarketplaceLoanDetailPage"));
const MyCommitmentsPage = lazyLoad(() => import("@/pages/MyCommitmentsPage"));
const StatementsPage = lazyLoad(() => import("@/pages/StatementsPage"));
const AdminStatementsPage = lazyLoad(() => import("@/pages/AdminStatementsPage"));
const RecentlyDeletedPage = lazyLoad(() => import("@/pages/RecentlyDeletedPage"));
const AdminBrokersPage = lazyLoad(() => import("@/pages/AdminBrokersPage"));
const BorrowerInvitesPage = lazyLoad(() => import("@/pages/BorrowerInvitesPage"));
const CreditCheckPublicPage = lazyLoad(() => import("@/pages/CreditCheckPublicPage"));
const CreditPullsPage = lazyLoad(() => import("@/pages/CreditPullsPage"));
const BrokerApplyLandingPage = lazyLoad(() => import("@/pages/BrokerApplyLandingPage"));
const ApplyDispatcherPage = lazyLoad(() => import("@/pages/ApplyDispatcherPage"));
const DirectApplyLandingPage = lazyLoad(() => import("@/pages/DirectApplyLandingPage"));
const HelocApplyPage = lazyLoad(() => import("@/pages/HelocApplyPage"));
const LoanBoardPage = lazyLoad(() => import("@/pages/LoanBoardPage"));
const InvestorReplyPage = lazyLoad(() => import("@/pages/InvestorReplyPage"));
const InvestorLinkPage = lazyLoad(() => import("@/pages/InvestorLinkPage"));
const ServicePage = lazyLoad(() => import("@/pages/ServicePage"));
const ContactPage = lazyLoad(() => import("@/pages/ContactPage"));
const FaqPage = lazyLoad(() => import("@/pages/FaqPage"));
const BlogPage = lazyLoad(() => import("@/pages/BlogPage"));
const LocationPage = lazyLoad(() => import("@/pages/LocationPage"));
const LocationsIndexPage = lazyLoad(() => import("@/pages/LocationsIndexPage"));
const FundingAppLayoutPage = lazyLoad(() => import("@/pages/FundingAppLayoutPage"));
const BorrowerPortalPage = lazyLoad(() => import("@/pages/BorrowerPortalPage"));
const BorrowerPortalBinderPage = lazyLoad(() => import("@/pages/BorrowerPortalBinderPage"));
const BorrowerFormPage = lazyLoad(() => import("@/pages/BorrowerFormPage"));
const BrokerFormsPage = lazyLoad(() => import("@/pages/BrokerFormsPage"));
const LoneStarFormsPage = lazyLoad(() => import("@/pages/LoneStarFormsPage"));
const RewardsPage = lazyLoad(() => import("@/pages/RewardsPage"));
const AdminSettingsPage = lazyLoad(() => import("@/pages/AdminSettingsPage"));
const MyAgencyPage = lazyLoad(() => import("@/pages/MyAgencyPage"));
const SubBrokerAcceptPage = lazyLoad(() => import("@/pages/SubBrokerAcceptPage"));
const NudgePreferencesPage = lazyLoad(() => import("@/pages/NudgePreferencesPage"));
const IncomeRequestPage = lazyLoad(() => import("@/pages/IncomeRequestPage"));
const InvoicePayPage = lazyLoad(() => import("@/pages/InvoicePayPage"));
const LenderResponsePage = lazyLoad(() => import("@/pages/LenderResponsePage"));
const InvoicesDashboardPage = lazyLoad(() => import("@/pages/InvoicesDashboardPage"));
const CheckRegisterPage = lazyLoad(() => import("@/pages/CheckRegisterPage"));
const BrokerRecruitLandingPage = lazyLoad(() => import("@/pages/BrokerRecruitLandingPage"));
const AdminRecruitLeadsPage = lazyLoad(() => import("@/pages/AdminRecruitLeadsPage"));
const MarketingDownloadsPage = lazyLoad(() => import("@/pages/MarketingDownloadsPage"));
const PublicDownloadsPage = lazyLoad(() => import("@/pages/PublicDownloadsPage"));
const BrokerDocumentsPage = lazyLoad(() => import("@/pages/BrokerDocumentsPage"));
const IsoAgreementsPage = lazyLoad(() => import("@/pages/IsoAgreementsPage"));
const IsoSignPage = lazyLoad(() => import("@/pages/IsoSignPage"));
const CRMContactsPage = lazyLoad(() => import("@/pages/CRMContactsPage"));
const CRMTemplatesPage = lazyLoad(() => import("@/pages/CRMTemplatesPage"));
const StatusTriggersPage = lazyLoad(() => import("@/pages/StatusTriggersPage"));
const CRMActivityPage = lazyLoad(() => import("@/pages/CRMActivityPage"));
const EmailCampaignsPage = lazyLoad(() => import("@/pages/EmailCampaignsPage"));
const EmailMarketingPage = lazyLoad(() => import("@/pages/EmailMarketingPage"));
const InboxPage = lazyLoad(() => import("@/pages/InboxPage"));
const GetFundedPage = lazyLoad(() => import("@/pages/GetFundedPage"));
const LeadManagementPage = lazyLoad(() => import("@/pages/LeadManagementPage"));
const TrackApplicationPage = lazyLoad(() => import("@/pages/TrackApplicationPage"));
const AttributionReportPage = lazyLoad(() => import("@/pages/AttributionReportPage"));
const ForgotPasswordPage = lazyLoad(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazyLoad(() => import("@/pages/ResetPasswordPage"));
const BrokerUserManual = lazyLoad(() => import("@/pages/BrokerUserManual"));
const AdminUserManual = lazyLoad(() => import("@/pages/AdminUserManual"));
const TrainingAdminPage = lazyLoad(() => import("@/pages/TrainingAdminPage"));
const BrokerTrainingPage = lazyLoad(() => import("@/pages/BrokerTrainingPage"));
const TrainingCenterPage = lazyLoad(() => import("@/pages/TrainingCenterPage"));
const TrainingModulePage = lazyLoad(() => import("@/pages/TrainingModulePage"));
const RecommenderAnalyticsPage = lazyLoad(() => import("@/pages/RecommenderAnalyticsPage"));
const TermsPage = lazyLoad(() => import("@/pages/LegalPages").then((m) => ({ default: m.TermsPage })));
const PrivacyPage = lazyLoad(() => import("@/pages/LegalPages").then((m) => ({ default: m.PrivacyPage })));
const CookiePolicyPage = lazyLoad(() => import("@/pages/LegalPages").then((m) => ({ default: m.CookiePolicyPage })));
const LegalRepositoryPage = lazyLoad(() => import("@/pages/LegalPages").then((m) => ({ default: m.LegalRepositoryPage })));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#F5F7FB" }} data-testid="page-loader">
      <div
        style={{
          width: 28,
          height: 28,
          border: "3px solid #D9E0EB",
          borderTopColor: "#1D3557",
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }}
      />
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  // Authenticated users are routed to their workspace; everyone else gets the
  // public marketing/landing experience with the borrower CTA.
  if (user) {
    if (user.role === "investor") return <Navigate to="/marketplace" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return <MarketingHomePage />;
}

function App() {
  // Polls /api/version every minute and prompts a refresh when a new
  // deploy is detected mid-session. Pairs with the no-cache meta tags
  // on index.html so the HTML shell + bundles stay in sync after deploy.
  useVersionPoll();
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <CookieBanner />
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/investor-reply/:token" element={<InvestorReplyPage />} />
            <Route path="/investor-link/:token" element={<InvestorLinkPage />} />
            <Route path="/apply/:slugOrToken" element={<ApplyDispatcherPage />} />
            <Route path="/credit-check/:token" element={<CreditCheckPublicPage />} />
            <Route path="/credit-check" element={<CreditCheckPublicPage />} />
            <Route path="/apply" element={<DirectApplyLandingPage />} />
            <Route path="/heloc" element={<HelocApplyPage />} />
            <Route path="/apply/heloc" element={<Navigate to="/heloc" replace />} />
            <Route path="/get-funded" element={<GetFundedPage />} />
            <Route path="/get-funded/:slug" element={<GetFundedPage />} />
            <Route path="/b/:slug" element={<BrokerApplyLandingPage />} />
            <Route path="/portal/:token/closing-binder" element={<BorrowerPortalBinderPage />} />
            <Route path="/portal/:token" element={<BorrowerPortalPage />} />
            <Route path="/forms/:token" element={<BorrowerFormPage />} />
            <Route path="/sub-broker-invite/:token" element={<SubBrokerAcceptPage />} />
            <Route path="/iso/:token" element={<IsoSignPage />} />
            <Route path="/nudge-preferences/:token" element={<NudgePreferencesPage />} />
            <Route path="/income/:token" element={<IncomeRequestPage />} />
            <Route path="/pay/invoice/:token" element={<InvoicePayPage />} />
            <Route path="/lender-response/:token" element={<LenderResponsePage />} />
            <Route path="/broker-recruit" element={<BrokerRecruitLandingPage />} />
            <Route path="/services/:slug" element={<ServicePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            <Route path="/legal" element={<LegalRepositoryPage />} />
            <Route path="/faqs" element={<Navigate to="/faq" replace />} />
            <Route path="/locations" element={<LocationsIndexPage />} />
            <Route path="/locations/:slug" element={<LocationPage />} />
            {/* Legacy WordPress URLs → new pages (preserves SEO during cutover) */}
            <Route path="/working-capital-loan" element={<Navigate to="/services/working-capital-loans" replace />} />
            <Route path="/business-loans" element={<Navigate to="/services/working-capital-loans" replace />} />
            <Route path="/merchant-cash-advance" element={<Navigate to="/services/merchant-cash-advance" replace />} />
            <Route path="/equipment-financing" element={<Navigate to="/services/equipment-financing" replace />} />
            <Route path="/commercial-real-estate-loans" element={<Navigate to="/services/commercial-real-estate-loans" replace />} />
            <Route path="/sba-loans-in-greensboro-nc" element={<Navigate to="/services/sba-loans" replace />} />
            <Route path="/contact-us" element={<Navigate to="/contact" replace />} />
            <Route path="/downloads" element={<PublicDownloadsPage />} />
            <Route path="/track" element={<TrackApplicationPage />} />

            <Route
              path="/dashboard"
              element={<ProtectedRoute roles={["broker", "admin", "manager", "underwriter", "processor"]}><DashboardPage /></ProtectedRoute>}
            />
            <Route
              path="/loans"
              element={<ProtectedRoute><LoansListPage /></ProtectedRoute>}
            />
            <Route
              path="/loans/new"
              element={<ProtectedRoute roles={["broker", "admin", "manager"]}><NewLoanPage /></ProtectedRoute>}
            />
            <Route
              path="/loans/archived"
              element={<ProtectedRoute roles={["admin", "manager"]}><ArchivedLoansPage /></ProtectedRoute>}
            />
            <Route
              path="/loans/:id/edit"
              element={<ProtectedRoute roles={["broker", "admin", "manager"]}><NewLoanPage /></ProtectedRoute>}
            />
            <Route
              path="/loans/:id"
              element={<ProtectedRoute><LoanDetailPage /></ProtectedRoute>}
            />
            <Route
              path="/loans/:id/application"
              element={<ProtectedRoute roles={["broker"]}><ApplicationFormPage /></ProtectedRoute>}
            />
            <Route
              path="/queue"
              element={
                <ProtectedRoute roles={["admin", "underwriter", "manager", "processor"]}>
                  <LoansListPage queueMode />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mca-pipeline"
              element={
                <ProtectedRoute roles={["admin", "manager", "underwriter", "processor"]}>
                  <MCAPipelinePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/board"
              element={
                <ProtectedRoute roles={["broker", "admin", "underwriter", "manager", "processor"]}>
                  <LoanBoardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/team"
              element={<ProtectedRoute roles={["admin", "manager"]}><TeamPage /></ProtectedRoute>}
            />
            <Route
              path="/admin/funding-app-layout"
              element={<ProtectedRoute roles={["admin", "manager"]}><FundingAppLayoutPage /></ProtectedRoute>}
            />
            <Route
              path="/investors"
              element={<ProtectedRoute roles={["admin", "manager", "underwriter", "processor"]}><InvestorsPage /></ProtectedRoute>}
            />
            <Route
              path="/lenders"
              element={<ProtectedRoute roles={["admin", "manager", "underwriter", "processor"]}><LendersPage /></ProtectedRoute>}
            />
            <Route
              path="/settings"
              element={<ProtectedRoute roles={["broker"]}><BrokerSettingsPage /></ProtectedRoute>}
            />
            <Route
              path="/admin/settings"
              element={<ProtectedRoute roles={["admin"]}><AdminSettingsPage /></ProtectedRoute>}
            />
            <Route
              path="/commissions"
              element={<ProtectedRoute roles={["broker", "admin", "manager"]}><CommissionsPage /></ProtectedRoute>}
            />
            <Route
              path="/payouts"
              element={<ProtectedRoute roles={["broker", "admin", "manager"]}><PayoutsPage /></ProtectedRoute>}
            />
            <Route
              path="/invoices"
              element={<ProtectedRoute roles={["admin", "manager", "underwriter", "processor"]}><InvoicesDashboardPage /></ProtectedRoute>}
            />
            <Route
              path="/admin/check-register"
              element={<ProtectedRoute roles={["admin", "manager"]}><CheckRegisterPage /></ProtectedRoute>}
            />
            <Route
              path="/admin/recruit-leads"
              element={<ProtectedRoute roles={["admin", "manager"]}><AdminRecruitLeadsPage /></ProtectedRoute>}
            />
            <Route
              path="/admin/marketing-downloads"
              element={<ProtectedRoute roles={["admin", "manager"]}><MarketingDownloadsPage /></ProtectedRoute>}
            />
            {/* CRM — brokers + admin/manager. Three sub-pages. */}
            <Route
              path="/crm/contacts"
              element={<ProtectedRoute roles={["admin", "manager", "broker"]}><CRMContactsPage /></ProtectedRoute>}
            />
            <Route
              path="/crm/templates"
              element={<ProtectedRoute roles={["admin", "manager", "broker"]}><CRMTemplatesPage /></ProtectedRoute>}
            />
            <Route
              path="/automations"
              element={<ProtectedRoute roles={["admin", "manager", "broker"]}><StatusTriggersPage /></ProtectedRoute>}
            />
            <Route
              path="/crm/activity"
              element={<ProtectedRoute roles={["admin", "manager", "broker"]}><CRMActivityPage /></ProtectedRoute>}
            />
            <Route
              path="/crm/campaigns"
              element={<ProtectedRoute roles={["admin", "manager", "broker", "underwriter", "processor"]}><EmailCampaignsPage /></ProtectedRoute>}            />
            <Route
              path="/marketing/email"
              element={<ProtectedRoute roles={["admin", "manager"]}><EmailMarketingPage /></ProtectedRoute>}
            />
            <Route
              path="/crm/leads"
              element={<ProtectedRoute roles={["admin", "manager", "broker"]}><LeadManagementPage /></ProtectedRoute>}
            />
            <Route
              path="/crm/attribution"
              element={<ProtectedRoute roles={["admin", "manager"]}><AttributionReportPage /></ProtectedRoute>}
            />
            <Route path="/crm" element={<Navigate to="/crm/contacts" replace />} />
            <Route
              path="/inbox"
              element={<ProtectedRoute roles={["admin", "manager", "broker", "underwriter", "processor"]}><InboxPage /></ProtectedRoute>}
            />
            <Route
              path="/help/broker"
              element={<ProtectedRoute roles={["broker"]}><BrokerUserManual /></ProtectedRoute>}
            />
            <Route
              path="/help/admin"
              element={<ProtectedRoute roles={["admin", "manager", "underwriter", "processor"]}><AdminUserManual /></ProtectedRoute>}
            />
            <Route
              path="/admin/trainings"
              element={<ProtectedRoute roles={["admin"]}><TrainingAdminPage /></ProtectedRoute>}
            />
            <Route
              path="/admin/recommender-analytics"
              element={<ProtectedRoute roles={["admin", "manager"]}><RecommenderAnalyticsPage /></ProtectedRoute>}
            />
            <Route
              path="/training"
              element={<ProtectedRoute roles={["broker"]}><BrokerTrainingPage /></ProtectedRoute>}
            />
            <Route
              path="/training/courses"
              element={<ProtectedRoute roles={["broker", "admin", "manager"]}><TrainingCenterPage /></ProtectedRoute>}
            />
            <Route
              path="/training/courses/:slug"
              element={<ProtectedRoute roles={["broker", "admin", "manager"]}><TrainingModulePage /></ProtectedRoute>}
            />
            <Route
              path="/my-team"
              element={<ProtectedRoute roles={["broker"]}><MyAgencyPage /></ProtectedRoute>}
            />
            {/* Back-compat: old "/my-agency" bookmarks + email links keep working. */}
            <Route path="/my-agency" element={<Navigate to="/my-team" replace />} />
            <Route
              path="/marketplace"
              element={<ProtectedRoute roles={["investor", "admin", "manager", "underwriter", "processor", "broker"]}><MarketplacePage /></ProtectedRoute>}
            />
            <Route
              path="/marketplace/:id"
              element={<ProtectedRoute roles={["investor", "admin", "manager", "underwriter", "processor", "broker"]}><MarketplaceLoanDetailPage /></ProtectedRoute>}
            />
            {/* Alias — backend invite emails link to /marketplace/loans/{id} */}
            <Route
              path="/marketplace/loans/:id"
              element={<ProtectedRoute roles={["investor", "admin", "manager", "underwriter", "processor", "broker"]}><MarketplaceLoanDetailPage /></ProtectedRoute>}
            />
            <Route
              path="/my-commitments"
              element={<ProtectedRoute roles={["investor"]}><MyCommitmentsPage /></ProtectedRoute>}
            />
            <Route
              path="/statements"
              element={<ProtectedRoute roles={["investor"]}><StatementsPage /></ProtectedRoute>}
            />
            <Route
              path="/admin/investor-statements"
              element={<ProtectedRoute roles={["admin", "manager"]}><AdminStatementsPage /></ProtectedRoute>}
            />
            <Route
              path="/admin/recently-deleted"
              element={<ProtectedRoute roles={["admin", "manager"]}><RecentlyDeletedPage /></ProtectedRoute>}
            />
            <Route
              path="/admin/brokers"
              element={<ProtectedRoute roles={["admin", "manager"]}><AdminBrokersPage /></ProtectedRoute>}
            />
            <Route
              path="/admin/broker-documents"
              element={<ProtectedRoute roles={["admin", "manager"]}><BrokerDocumentsPage /></ProtectedRoute>}
            />
            <Route
              path="/admin/iso-agreements"
              element={<ProtectedRoute roles={["admin", "manager"]}><IsoAgreementsPage /></ProtectedRoute>}
            />
            <Route
              path="/borrower-invites"
              element={<ProtectedRoute roles={["broker"]}><BorrowerInvitesPage /></ProtectedRoute>}
            />
            <Route
              path="/credit-pulls"
              element={<ProtectedRoute roles={["broker", "admin", "manager", "underwriter", "processor"]}><CreditPullsPage /></ProtectedRoute>}
            />
            <Route
              path="/broker/forms"
              element={<ProtectedRoute roles={["broker", "admin", "manager", "underwriter", "processor"]}><BrokerFormsPage /></ProtectedRoute>}
            />
            <Route
              path="/lone-star"
              element={<ProtectedRoute roles={["admin", "manager"]}><LoneStarFormsPage /></ProtectedRoute>}
            />
            <Route
              path="/rewards"
              element={<ProtectedRoute roles={["admin"]}><RewardsPage /></ProtectedRoute>}
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
